import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/context/AuthContext';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { addDoc, collection, onSnapshot, orderBy, query, serverTimestamp, Timestamp } from 'firebase/firestore';

const db = getFirebaseFirestore();
const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
import {
  FileText,
  Download,
  Mail,
  Calendar,
  Clock,
  ChevronDown,
  Filter,
  BarChart3,
  PieChart,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Package,
  Users,
  Target,
  RefreshCw,
  Check,
  X,
  Eye,
  Send,
  FileDown,
  CalendarRange,
  Printer,
  Share2,
  MoreVertical,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Building2,
  Zap,
  Plus,
  UserPlus,
  Copy,
  EyeOff,
  Crown,
  Lightbulb,
  ChevronRight
} from 'lucide-react';

// Types
interface Report {
  id: string;
  name: string;
  type: 'sales' | 'inventory' | 'performance' | 'financial' | 'custom' | 'weekly' | 'ceo-summary' | 'opportunity';
  period: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
  status: 'ready' | 'generating' | 'scheduled' | 'failed';
  createdAt: Date;
  size?: string;
  platforms?: string[];
  metrics?: string[];
  content?: string;
  sentAt?: Date | null;
}

interface ScheduledReport {
  id: string;
  reportType: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  nextRun: Date;
  recipients: string[];
  enabled: boolean;
}

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  metrics: string[];
}

interface EmailRecipient {
  id: string;
  email: string;
  type: 'to' | 'cc' | 'bcc';
}

interface EmailConfig {
  recipients: EmailRecipient[];
}

// Mock data
const MOCK_REPORTS: Report[] = [
  {
    id: '1',
    name: 'Haftalık Satış Raporu',
    type: 'sales',
    period: 'weekly',
    status: 'ready',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2),
    size: '2.4 MB',
    platforms: ['Trendyol', 'Hepsiburada', 'Amazon TR'],
    metrics: ['Satış', 'Sipariş', 'Gelir']
  },
  {
    id: '2',
    name: 'Aylık Performans Raporu',
    type: 'performance',
    period: 'monthly',
    status: 'ready',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
    size: '5.8 MB',
    platforms: ['Tüm Platformlar'],
    metrics: ['ROAS', 'Dönüşüm', 'Trafik']
  },
  {
    id: '3',
    name: 'Stok Durum Raporu',
    type: 'inventory',
    period: 'daily',
    status: 'generating',
    createdAt: new Date(),
    platforms: ['Trendyol'],
    metrics: ['Stok', 'Kritik Ürünler']
  },
  {
    id: '4',
    name: 'Finansal Özet',
    type: 'financial',
    period: 'monthly',
    status: 'ready',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48),
    size: '1.2 MB',
    platforms: ['Tüm Platformlar'],
    metrics: ['Gelir', 'Gider', 'Kar']
  },
  {
    id: '5',
    name: 'Q4 2024 Özet Raporu',
    type: 'custom',
    period: 'quarterly',
    status: 'ready',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72),
    size: '8.5 MB',
    platforms: ['Tüm Platformlar'],
    metrics: ['Tüm Metrikler']
  }
];

const MOCK_SCHEDULED: ScheduledReport[] = [
  {
    id: '1',
    reportType: 'Haftalık Satış Raporu',
    frequency: 'weekly',
    nextRun: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3),
    recipients: ['info@sirket.com', 'yonetim@sirket.com'],
    enabled: true
  },
  {
    id: '2',
    reportType: 'Aylık Performans Raporu',
    frequency: 'monthly',
    nextRun: new Date(Date.now() + 1000 * 60 * 60 * 24 * 15),
    recipients: ['info@sirket.com'],
    enabled: true
  },
  {
    id: '3',
    reportType: 'Günlük Stok Özeti',
    frequency: 'daily',
    nextRun: new Date(Date.now() + 1000 * 60 * 60 * 8),
    recipients: ['depo@sirket.com'],
    enabled: false
  }
];

const REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'ceo-summary',
    name: 'CEO Özeti',
    description: 'Bu hafta ne kazandın, ne kaybettin, neye dikkat etmelisin — tek sayfada',
    icon: Crown,
    color: 'amber',
    metrics: ['Haftalık Kazanım', 'Kayıp Alanlar', 'Kritik Uyarılar', 'Öncelikli Aksiyonlar']
  },
  {
    id: 'opportunity',
    name: 'Fırsat Raporu',
    description: '"Bu 3 şeyi yapsan X₺ kazanırsın" — somut aksiyon önerileri',
    icon: Lightbulb,
    color: 'emerald',
    metrics: ['Fırsatlar', 'Tahmini Kazanım', 'Kolay Kazanımlar', 'Aksiyonlar']
  },
  {
    id: 'sales',
    name: 'Satış Raporu',
    description: 'Satış performansı, sipariş sayıları ve gelir analizi',
    icon: ShoppingCart,
    color: 'green',
    metrics: ['Toplam Satış', 'Sipariş Sayısı', 'Ortalama Sepet', 'İade Oranı']
  },
  {
    id: 'inventory',
    name: 'Stok Raporu',
    description: 'Stok seviyeleri, kritik ürünler ve envanter analizi',
    icon: Package,
    color: 'blue',
    metrics: ['Stok Durumu', 'Kritik Ürünler', 'Stok Devir Hızı', 'Değer Analizi']
  },
  {
    id: 'performance',
    name: 'Performans Raporu',
    description: 'Platform performansı, ROAS ve dönüşüm oranları',
    icon: TrendingUp,
    color: 'purple',
    metrics: ['ROAS', 'Dönüşüm Oranı', 'Trafik', 'Reklam Harcaması']
  },
  {
    id: 'financial',
    name: 'Finansal Rapor',
    description: 'Gelir, gider, kar marjı ve finansal özet',
    icon: DollarSign,
    color: 'yellow',
    metrics: ['Brüt Gelir', 'Net Kar', 'Komisyonlar', 'Kar Marjı']
  },
  {
    id: 'customer',
    name: 'Müşteri Raporu',
    description: 'Müşteri analizi, segmentasyon ve sadakat',
    icon: Users,
    color: 'cyan',
    metrics: ['Yeni Müşteri', 'Tekrar Satın Alma', 'LTV', 'Churn Oranı']
  },
  {
    id: 'custom',
    name: 'Özel Rapor',
    description: 'İstediğiniz metrikleri seçerek özel rapor oluşturun',
    icon: BarChart3,
    color: 'orange',
    metrics: ['Özelleştirilebilir']
  }
];

type ViewMode = 'reports' | 'create' | 'scheduled';

const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('reports');
  const [reports, setReports] = useState<Report[]>([]);
  const [scheduledReports, setScheduledReports] = useState<ScheduledReport[]>(MOCK_SCHEDULED);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [activeReport, setActiveReport] = useState<Report | null>(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Email configuration state
  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    recipients: [
      { id: '1', email: 'info@sirket.com', type: 'to' },
      { id: '2', email: 'yonetim@sirket.com', type: 'to' },
    ]
  });
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailType, setNewEmailType] = useState<'to' | 'cc' | 'bcc'>('to');
  const [emailError, setEmailError] = useState<string | null>(null);

  const reportRef = useRef<HTMLDivElement>(null);

  // Firestore'dan raporları gerçek zamanlı dinle
  useEffect(() => {
    if (!user?.uid) {
      // Kullanıcı yoksa mock göster
      setReports(MOCK_REPORTS);
      setIsLoading(false);
      return;
    }

    const reportsRef = collection(db, 'users', user.uid, 'ai_reports');
    const q = query(reportsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firestoreReports: Report[] = snapshot.docs.map(doc => {
        const data = doc.data();
        const createdAt = data.createdAt instanceof Timestamp
          ? data.createdAt.toDate()
          : new Date(data.createdAt || Date.now());
        return {
          id: doc.id,
          name: data.title || 'Rapor',
          type: data.type || 'weekly',
          period: 'weekly',
          status: 'ready',
          createdAt,
          platforms: data.platforms || [],
          content: data.content,
          sentAt: data.sentAt ? (data.sentAt instanceof Timestamp ? data.sentAt.toDate() : null) : null,
        } as Report;
      });

      // Firestore raporları yoksa mock ekle
      setReports(firestoreReports.length > 0 ? firestoreReports : MOCK_REPORTS);
      setIsLoading(false);
    }, () => {
      setReports(MOCK_REPORTS);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (date: Date): string => {
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getStatusBadge = (status: Report['status']) => {
    switch (status) {
      case 'ready':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            Hazır
          </span>
        );
      case 'generating':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            Oluşturuluyor
          </span>
        );
      case 'scheduled':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-purple-500/20 text-purple-400 rounded-full">
            <Clock className="w-3 h-3" />
            Planlandı
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
            <AlertCircle className="w-3 h-3" />
            Başarısız
          </span>
        );
    }
  };

  const getTypeIcon = (type: Report['type']) => {
    switch (type) {
      case 'ceo-summary':
        return <Crown className="w-5 h-5 text-amber-400" />;
      case 'opportunity':
        return <Lightbulb className="w-5 h-5 text-emerald-400" />;
      case 'sales':
        return <ShoppingCart className="w-5 h-5 text-green-400" />;
      case 'inventory':
        return <Package className="w-5 h-5 text-blue-400" />;
      case 'performance':
        return <TrendingUp className="w-5 h-5 text-purple-400" />;
      case 'financial':
        return <DollarSign className="w-5 h-5 text-yellow-400" />;
      default:
        return <BarChart3 className="w-5 h-5 text-orange-400" />;
    }
  };

  const handleDownloadPDF = (report: Report) => {
    // Simulate PDF download
    const element = document.createElement('a');

    // Create a simple PDF-like content (in real app, use jsPDF or similar)
    const content = `
RAPOR: ${report.name}
Oluşturulma: ${formatDate(report.createdAt)}
Dönem: ${report.period}
Platformlar: ${report.platforms?.join(', ')}
Metrikler: ${report.metrics?.join(', ')}

---
Bu demo bir rapordur.
Gerçek veriler bağlantılardan çekilecektir.
    `;

    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${report.name.replace(/\s+/g, '_')}_${formatShortDate(report.createdAt)}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handlePrint = (report: Report) => {
    const printContent = `
      <html>
        <head>
          <title>${report.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #333; border-bottom: 2px solid #7c3aed; padding-bottom: 10px; }
            .info { margin: 20px 0; color: #666; }
            .metrics { display: flex; gap: 20px; margin: 30px 0; }
            .metric { padding: 20px; background: #f5f5f5; border-radius: 8px; flex: 1; }
            .metric-value { font-size: 24px; font-weight: bold; color: #333; }
            .metric-label { color: #666; font-size: 14px; }
          </style>
        </head>
        <body>
          <h1>${report.name}</h1>
          <div class="info">
            <p><strong>Dönem:</strong> ${report.period}</p>
            <p><strong>Oluşturulma:</strong> ${formatDate(report.createdAt)}</p>
            <p><strong>Platformlar:</strong> ${report.platforms?.join(', ')}</p>
          </div>
          <div class="metrics">
            <div class="metric">
              <div class="metric-value">157.100 ₺</div>
              <div class="metric-label">Toplam Gelir</div>
            </div>
            <div class="metric">
              <div class="metric-value">539</div>
              <div class="metric-label">Sipariş</div>
            </div>
            <div class="metric">
              <div class="metric-value">291 ₺</div>
              <div class="metric-label">Ort. Sepet</div>
            </div>
          </div>
          <p style="color: #999; font-size: 12px; margin-top: 40px;">
            Bu rapor Bosphorus Labs E-ticaret Intelligence tarafından oluşturulmuştur.
          </p>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleSendEmail = () => {
    if (!emailAddress || !selectedReport) return;

    // Simulate email sending
    alert(`Rapor "${selectedReport.name}" ${emailAddress} adresine gönderildi.`);
    setShowEmailModal(false);
    setEmailAddress('');
    setSelectedReport(null);
  };

  const buildPrompt = (templateId: string, templateName: string, metrics: string[], periodStr: string, platforms: string[]): { prompt: string; useCase: string } => {
    const platformStr = platforms.length > 0 ? platforms.join(', ') : 'Tüm Platformlar';

    if (templateId === 'ceo-summary') {
      return {
        useCase: 'weekly_report',
        prompt: `CEO Özeti raporu oluştur. Dönem: ${periodStr}. Platformlar: ${platformStr}.

Rapor formatı (Markdown):
# CEO Özeti — ${periodStr}

## Bu Hafta Kazandıklarım
(Olumlu gelişmeler, artan metrikler, başarılar)

## Dikkat Etmem Gerekenler
(Düşen metrikler, riskler, kritik uyarılar)

## Bu Haftanın 3 Önceliği
1. [En kritik aksiyon]
2. [İkinci öncelik]
3. [Üçüncü öncelik]

## Kısa Özet
(2-3 cümle — patrona brifing gibi öz ve net)

Gerçek platform verilerini kullan. Türkçe, net ve özlü yaz.`,
      };
    }

    if (templateId === 'opportunity') {
      return {
        useCase: 'analysis',
        prompt: `Fırsat Raporu oluştur. Dönem: ${periodStr}. Platformlar: ${platformStr}.

Rapor formatı (Markdown):
# Fırsat Raporu — ${periodStr}

## Hemen Yapılabilecek Fırsatlar (Bu Hafta)
(Kolaydan zora sıralı, her biri için tahmini kazanım)

### Fırsat 1: [Başlık]
- **Ne yapılacak:** ...
- **Neden:** ...
- **Tahmini kazanım:** X₺ / %X artış

### Fırsat 2: [Başlık]
...

### Fırsat 3: [Başlık]
...

## Orta Vadeli Fırsatlar (Bu Ay)
(1-2 madde)

## Toplam Potansiyel Kazanım
**Bu hafta aksiyonlarla:** ~X₺ ek gelir potansiyeli

Gerçek platform verilerini kullan. Somut ve ölçülebilir öneriler sun. Türkçe yaz.`,
      };
    }

    return {
      useCase: 'chat',
      prompt: `${templateName} raporu oluştur. Dönem: ${periodStr}. Platformlar: ${platformStr}. Metrikler: ${metrics.join(', ')}. Türkçe, detaylı Markdown formatında yaz.`,
    };
  };

  const handleGenerateReport = async () => {
    if (!selectedTemplate) return;

    setIsGenerating(true);
    setGenerateError(null);

    try {
      const template = REPORT_TEMPLATES.find(t => t.id === selectedTemplate);
      const periodStr = dateRange.start && dateRange.end
        ? `${dateRange.start} - ${dateRange.end}`
        : formatShortDate(new Date());

      const { prompt, useCase } = buildPrompt(
        selectedTemplate,
        template?.name || '',
        template?.metrics || [],
        periodStr,
        selectedPlatforms
      );

      const res = await fetch(`${FUNCTIONS_URL}/callAI`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.uid || 'anonymous',
          useCase,
          messages: [{ role: 'user', content: prompt }],
          includePlatformData: true,
        }),
      });

      if (!res.ok) throw new Error('Rapor oluşturulamadı');
      const data = await res.json();

      const newReport: Report = {
        id: String(Date.now()),
        name: `${template?.name} — ${periodStr}`,
        type: selectedTemplate as Report['type'],
        period: dateRange.start ? 'custom' : 'weekly',
        status: 'ready',
        createdAt: new Date(),
        platforms: selectedPlatforms.length > 0 ? selectedPlatforms : ['Tüm Platformlar'],
        metrics: template?.metrics,
        content: data.response,
      };

      // Firestore'a da kaydet
      if (user?.uid && data.response) {
        try {
          const reportsRef = collection(db, 'users', user.uid, 'ai_reports');
          await addDoc(reportsRef, {
            title: newReport.name,
            type: newReport.type,
            content: data.response,
            period: periodStr,
            platforms: newReport.platforms,
            tokensUsed: data.tokensUsed || 0,
            model: data.model || 'gpt-4o-mini',
            createdAt: serverTimestamp(),
          });
        } catch {
          // Firestore kayıt hatası - rapor yine de göster
        }
      }

      setReports(prev => [newReport, ...prev]);
      setActiveReport(newReport);
      setViewMode('reports');
      setSelectedTemplate(null);
      setSelectedPlatforms([]);
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Rapor oluşturulamadı');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleScheduledReport = (id: string) => {
    setScheduledReports(prev => prev.map(r =>
      r.id === id ? { ...r, enabled: !r.enabled } : r
    ));
  };

  // Email management functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addEmailRecipient = () => {
    setEmailError(null);

    if (!newEmail.trim()) {
      setEmailError('E-posta adresi gerekli');
      return;
    }

    if (!validateEmail(newEmail.trim())) {
      setEmailError('Geçerli bir e-posta adresi girin');
      return;
    }

    // Check for duplicates
    if (emailConfig.recipients.some(r => r.email.toLowerCase() === newEmail.trim().toLowerCase())) {
      setEmailError('Bu e-posta adresi zaten ekli');
      return;
    }

    const newRecipient: EmailRecipient = {
      id: String(Date.now()),
      email: newEmail.trim(),
      type: newEmailType
    };

    setEmailConfig(prev => ({
      ...prev,
      recipients: [...prev.recipients, newRecipient]
    }));

    setNewEmail('');
    setNewEmailType('to');
    setShowAddEmailModal(false);
  };

  const removeEmailRecipient = (id: string) => {
    setEmailConfig(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r.id !== id)
    }));
  };

  const updateEmailType = (id: string, type: 'to' | 'cc' | 'bcc') => {
    setEmailConfig(prev => ({
      ...prev,
      recipients: prev.recipients.map(r =>
        r.id === id ? { ...r, type } : r
      )
    }));
  };

  const getEmailTypeLabel = (type: 'to' | 'cc' | 'bcc'): string => {
    switch (type) {
      case 'to': return 'Alıcı';
      case 'cc': return 'CC';
      case 'bcc': return 'Gizli CC';
    }
  };

  const getEmailTypeColor = (type: 'to' | 'cc' | 'bcc'): string => {
    switch (type) {
      case 'to': return 'bg-blue-500/20 text-blue-400';
      case 'cc': return 'bg-purple-500/20 text-purple-400';
      case 'bcc': return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Raporlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7 text-purple-400" />
            Raporlama
          </h1>
          <p className="text-gray-400 mt-1">Otomatik raporlar ve PDF export</p>
        </div>

        {/* View Switcher */}
        <div className="flex items-center gap-2 bg-[#1a1a2e] rounded-xl p-1">
          <button
            onClick={() => setViewMode('reports')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'reports'
                ? 'bg-purple-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <FileText className="w-4 h-4" />
            Raporlar
          </button>
          <button
            onClick={() => setViewMode('create')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'create'
                ? 'bg-purple-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Yeni Rapor
          </button>
          <button
            onClick={() => setViewMode('scheduled')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'scheduled'
                ? 'bg-purple-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Clock className="w-4 h-4" />
            Zamanlanmış
          </button>
        </div>
      </div>

      {viewMode === 'reports' && (
        <>
          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#1a1a2e]/50 rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{reports.length}</p>
                  <p className="text-xs text-gray-400">Toplam Rapor</p>
                </div>
              </div>
            </div>
            <div className="bg-[#1a1a2e]/50 rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{reports.filter(r => r.status === 'ready').length}</p>
                  <p className="text-xs text-gray-400">Hazır</p>
                </div>
              </div>
            </div>
            <div className="bg-[#1a1a2e]/50 rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{scheduledReports.filter(r => r.enabled).length}</p>
                  <p className="text-xs text-gray-400">Zamanlanmış</p>
                </div>
              </div>
            </div>
            <div className="bg-[#1a1a2e]/50 rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">12</p>
                  <p className="text-xs text-gray-400">Bu Ay Gönderilen</p>
                </div>
              </div>
            </div>
          </div>

          {/* Reports List + Detail Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* List */}
            <div className={`bg-[#1a1a2e]/50 rounded-2xl border border-white/5 overflow-hidden ${activeReport ? 'lg:col-span-2' : 'lg:col-span-5'}`}>
              <div className="p-4 border-b border-white/5">
                <h2 className="font-semibold text-white">Son Raporlar</h2>
              </div>

              <div className="divide-y divide-white/5">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className={`p-4 hover:bg-white/5 transition-colors cursor-pointer ${activeReport?.id === report.id ? 'bg-purple-500/10 border-l-2 border-purple-500' : ''}`}
                    onClick={() => setActiveReport(activeReport?.id === report.id ? null : report)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-[#12121a] flex items-center justify-center flex-shrink-0">
                          {getTypeIcon(report.type)}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-medium text-white truncate">{report.name}</h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(report.createdAt)}
                            </span>
                            {report.size && (
                              <span className="px-2 py-0.5 bg-white/5 rounded">
                                {report.size}
                              </span>
                            )}
                            {!activeReport && report.platforms?.map(p => (
                              <span key={p} className="px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded">
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(report.status)}
                        {report.content && (
                          <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${activeReport?.id === report.id ? 'rotate-90' : ''}`} />
                        )}

                        {!activeReport && report.status === 'ready' && (
                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => handleDownloadPDF(report)}
                              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                              title="PDF İndir"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedReport(report);
                                setShowEmailModal(true);
                              }}
                              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                              title="E-posta Gönder"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detail / Markdown Panel */}
            {activeReport && (
              <div className="lg:col-span-3 bg-[#1a1a2e]/50 rounded-2xl border border-white/5 overflow-hidden flex flex-col">
                {/* Panel Header */}
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-[#12121a] flex items-center justify-center flex-shrink-0">
                      {getTypeIcon(activeReport.type)}
                    </div>
                    <h3 className="font-semibold text-white truncate text-sm">{activeReport.name}</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {activeReport.status === 'ready' && (
                      <>
                        <button
                          onClick={() => handleDownloadPDF(activeReport)}
                          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                          title="İndir"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedReport(activeReport);
                            setShowEmailModal(true);
                          }}
                          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                          title="E-posta Gönder"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setActiveReport(null)}
                      className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Markdown Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {activeReport.content ? (
                    <div className="prose prose-invert prose-sm max-w-none
                      prose-headings:text-white prose-headings:font-bold
                      prose-h1:text-xl prose-h2:text-lg prose-h3:text-base
                      prose-p:text-gray-300 prose-p:leading-relaxed
                      prose-strong:text-white prose-strong:font-semibold
                      prose-li:text-gray-300
                      prose-ul:space-y-1 prose-ol:space-y-1
                      prose-blockquote:border-purple-500 prose-blockquote:text-gray-400
                      prose-code:text-purple-300 prose-code:bg-purple-500/10 prose-code:px-1 prose-code:rounded
                      prose-hr:border-white/10">
                      <ReactMarkdown>{activeReport.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                      <FileText className="w-12 h-12 mb-3 opacity-30" />
                      <p className="text-sm">Bu rapor için içerik bulunamadı</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {viewMode === 'create' && (
        <>
          {/* Report Templates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORT_TEMPLATES.map((template) => {
              const colors: Record<string, string> = {
                amber: 'from-amber-500/20 to-yellow-500/10 border-amber-500/20 hover:border-amber-500/40',
                emerald: 'from-emerald-500/20 to-green-500/10 border-emerald-500/20 hover:border-emerald-500/40',
                green: 'from-green-500/20 to-emerald-500/10 border-green-500/20 hover:border-green-500/40',
                blue: 'from-blue-500/20 to-cyan-500/10 border-blue-500/20 hover:border-blue-500/40',
                purple: 'from-purple-500/20 to-pink-500/10 border-purple-500/20 hover:border-purple-500/40',
                yellow: 'from-yellow-500/20 to-amber-500/10 border-yellow-500/20 hover:border-yellow-500/40',
                cyan: 'from-cyan-500/20 to-blue-500/10 border-cyan-500/20 hover:border-cyan-500/40',
                orange: 'from-orange-500/20 to-red-500/10 border-orange-500/20 hover:border-orange-500/40'
              };
              const iconColors: Record<string, string> = {
                amber: 'bg-amber-500/20 text-amber-400',
                emerald: 'bg-emerald-500/20 text-emerald-400',
                green: 'bg-green-500/20 text-green-400',
                blue: 'bg-blue-500/20 text-blue-400',
                purple: 'bg-purple-500/20 text-purple-400',
                yellow: 'bg-yellow-500/20 text-yellow-400',
                cyan: 'bg-cyan-500/20 text-cyan-400',
                orange: 'bg-orange-500/20 text-orange-400'
              };

              return (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(selectedTemplate === template.id ? null : template.id)}
                  className={`text-left p-5 rounded-2xl bg-gradient-to-br border transition-all ${colors[template.color]} ${
                    selectedTemplate === template.id ? 'ring-2 ring-purple-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColors[template.color]}`}>
                      <template.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1">{template.name}</h3>
                      <p className="text-sm text-gray-400 mb-3">{template.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {template.metrics.slice(0, 3).map((metric) => (
                          <span key={metric} className="px-2 py-0.5 text-xs bg-white/10 text-gray-300 rounded">
                            {metric}
                          </span>
                        ))}
                      </div>
                    </div>
                    {selectedTemplate === template.id && (
                      <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Report Configuration */}
          {selectedTemplate && (
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-6 border border-white/5 space-y-6">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Filter className="w-5 h-5 text-purple-400" />
                Rapor Ayarları
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Date Range */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Tarih Aralığı</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="flex-1 px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="flex-1 px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                </div>

                {/* Platforms */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Platformlar</label>
                  <div className="flex flex-wrap gap-2">
                    {['Trendyol', 'Hepsiburada', 'Amazon TR', 'n11', 'Çiçeksepeti', 'PttAVM', 'Google Ads', 'Meta Ads'].map((platform) => (
                      <button
                        key={platform}
                        onClick={() => setSelectedPlatforms(prev =>
                          prev.includes(platform)
                            ? prev.filter(p => p !== platform)
                            : [...prev, platform]
                        )}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                          selectedPlatforms.includes(platform)
                            ? 'bg-purple-500 text-white'
                            : 'bg-[#12121a] text-gray-400 border border-white/10'
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <div className="flex flex-col items-end gap-3 pt-4 border-t border-white/5">
                {generateError && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400 w-full">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {generateError}
                  </div>
                )}
                <button
                  onClick={handleGenerateReport}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Rapor Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      Rapor Oluştur
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {viewMode === 'scheduled' && (
        <>
          {/* Scheduled Reports */}
          <div className="bg-[#1a1a2e]/50 rounded-2xl border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="font-semibold text-white">Zamanlanmış Raporlar</h2>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 transition-colors">
                <CalendarRange className="w-4 h-4" />
                Yeni Zamanlama
              </button>
            </div>

            <div className="divide-y divide-white/5">
              {scheduledReports.map((scheduled) => (
                <div
                  key={scheduled.id}
                  className="p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        scheduled.enabled ? 'bg-green-500/20' : 'bg-gray-500/20'
                      }`}>
                        <Clock className={`w-5 h-5 ${scheduled.enabled ? 'text-green-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">{scheduled.reportType}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            {scheduled.frequency === 'daily' ? 'Günlük' :
                             scheduled.frequency === 'weekly' ? 'Haftalık' : 'Aylık'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Sonraki: {formatShortDate(scheduled.nextRun)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {scheduled.recipients.length} alıcı
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleScheduledReport(scheduled.id)}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          scheduled.enabled ? 'bg-purple-500' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                          scheduled.enabled ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Email Configuration */}
          <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Mail className="w-6 h-6 text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-1">E-posta Yapılandırması</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Otomatik raporların gönderileceği e-posta adreslerini yapılandırın.
                </p>

                {/* Recipients by Type */}
                <div className="space-y-4">
                  {/* To Recipients */}
                  {emailConfig.recipients.filter(r => r.type === 'to').length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium text-blue-400">Alıcılar (To)</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {emailConfig.recipients.filter(r => r.type === 'to').map(recipient => (
                          <div
                            key={recipient.id}
                            className="group flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-gray-300"
                          >
                            <span>{recipient.email}</span>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => updateEmailType(recipient.id, 'cc')}
                                className="p-0.5 text-gray-500 hover:text-purple-400"
                                title="CC'ye taşı"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => updateEmailType(recipient.id, 'bcc')}
                                className="p-0.5 text-gray-500 hover:text-gray-300"
                                title="Gizli CC'ye taşı"
                              >
                                <EyeOff className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => removeEmailRecipient(recipient.id)}
                                className="p-0.5 text-gray-500 hover:text-red-400"
                                title="Kaldır"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CC Recipients */}
                  {emailConfig.recipients.filter(r => r.type === 'cc').length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Copy className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium text-purple-400">CC (Carbon Copy)</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {emailConfig.recipients.filter(r => r.type === 'cc').map(recipient => (
                          <div
                            key={recipient.id}
                            className="group flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-sm text-gray-300"
                          >
                            <span>{recipient.email}</span>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => updateEmailType(recipient.id, 'to')}
                                className="p-0.5 text-gray-500 hover:text-blue-400"
                                title="Alıcıya taşı"
                              >
                                <Mail className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => updateEmailType(recipient.id, 'bcc')}
                                className="p-0.5 text-gray-500 hover:text-gray-300"
                                title="Gizli CC'ye taşı"
                              >
                                <EyeOff className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => removeEmailRecipient(recipient.id)}
                                className="p-0.5 text-gray-500 hover:text-red-400"
                                title="Kaldır"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* BCC Recipients */}
                  {emailConfig.recipients.filter(r => r.type === 'bcc').length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <EyeOff className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-400">Gizli CC (BCC)</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {emailConfig.recipients.filter(r => r.type === 'bcc').map(recipient => (
                          <div
                            key={recipient.id}
                            className="group flex items-center gap-2 px-3 py-1.5 bg-gray-500/10 border border-gray-500/20 rounded-lg text-sm text-gray-300"
                          >
                            <span>{recipient.email}</span>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => updateEmailType(recipient.id, 'to')}
                                className="p-0.5 text-gray-500 hover:text-blue-400"
                                title="Alıcıya taşı"
                              >
                                <Mail className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => updateEmailType(recipient.id, 'cc')}
                                className="p-0.5 text-gray-500 hover:text-purple-400"
                                title="CC'ye taşı"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => removeEmailRecipient(recipient.id)}
                                className="p-0.5 text-gray-500 hover:text-red-400"
                                title="Kaldır"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Button */}
                  <button
                    onClick={() => setShowAddEmailModal(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-dashed border-white/20 rounded-xl text-sm text-gray-400 hover:text-white hover:border-purple-500/50 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Yeni E-posta Ekle
                  </button>
                </div>

                {/* Summary */}
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-4 text-xs text-gray-500">
                  <span>{emailConfig.recipients.filter(r => r.type === 'to').length} Alıcı</span>
                  <span>{emailConfig.recipients.filter(r => r.type === 'cc').length} CC</span>
                  <span>{emailConfig.recipients.filter(r => r.type === 'bcc').length} Gizli CC</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Email Modal */}
      {showAddEmailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-md border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-purple-400" />
                Yeni E-posta Ekle
              </h3>
              <button
                onClick={() => {
                  setShowAddEmailModal(false);
                  setNewEmail('');
                  setNewEmailType('to');
                  setEmailError(null);
                }}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Error Message */}
              {emailError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {emailError}
                </div>
              )}

              {/* Email Input */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">E-posta Adresi</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-4 py-3 bg-[#12121a] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                />
              </div>

              {/* Type Selection */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Alıcı Türü</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'to', label: 'Alıcı (To)', icon: Mail, color: 'blue' },
                    { id: 'cc', label: 'CC', icon: Copy, color: 'purple' },
                    { id: 'bcc', label: 'Gizli CC', icon: EyeOff, color: 'gray' },
                  ].map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setNewEmailType(type.id as 'to' | 'cc' | 'bcc')}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                        newEmailType === type.id
                          ? `bg-${type.color}-500/20 border-${type.color}-500/50 text-${type.color}-400`
                          : 'bg-[#12121a] border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                    >
                      <type.icon className="w-4 h-4" />
                      <span className="text-xs">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Type Description */}
              <div className="p-3 bg-white/5 rounded-xl text-xs text-gray-400">
                {newEmailType === 'to' && (
                  <p><strong>Alıcı:</strong> E-posta doğrudan bu adrese gönderilir.</p>
                )}
                {newEmailType === 'cc' && (
                  <p><strong>CC:</strong> Bu adres e-postanın bir kopyasını alır ve tüm alıcılar bunu görebilir.</p>
                )}
                {newEmailType === 'bcc' && (
                  <p><strong>Gizli CC:</strong> Bu adres e-postanın bir kopyasını alır ancak diğer alıcılar bunu göremez.</p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowAddEmailModal(false);
                    setNewEmail('');
                    setNewEmailType('to');
                    setEmailError(null);
                  }}
                  className="flex-1 px-4 py-3 border border-white/10 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={addEmailRecipient}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedReport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-md border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Raporu E-posta ile Gönder</h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-3 bg-[#12121a] rounded-xl">
                <p className="text-sm text-gray-400">Rapor</p>
                <p className="text-white font-medium">{selectedReport.name}</p>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-2 block">E-posta Adresi</label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-4 py-3 bg-[#12121a] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="flex-1 px-4 py-3 border border-white/10 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={!emailAddress}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Gönder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
